import { Link } from 'react-router-dom';
import { formatDate, formatTime } from '../../../lib/formatDate';
import Chevron from '../ui/Chevron';

export const CATEGORY_LABELS = {
  requests: 'Requests',
  access: 'Access',
  apps: 'Apps',
  profile: 'Profile',
  admin: 'Admin actions',
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "Today" / "Yesterday" / dd/mm/yyyy, in the viewer's local day. */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (dayKey(d) === dayKey(now)) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (dayKey(d) === dayKey(y)) return 'Yesterday';
  return formatDate(d);
}

/** Consecutive entries grouped under their day label (entries are newest first). */
export function groupByDay(entries, now = new Date()) {
  const groups = [];
  for (const e of entries) {
    const label = dayLabel(e.at, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(e);
    else groups.push({ label, items: [e] });
  }
  return groups;
}

function entryLink(entry, { isSelf, canReviewApps }) {
  if (entry.entity?.type !== 'app_request' || !entry.entity.id) return null;
  if (canReviewApps) return `/admin/apps/${entry.entity.id}`;
  if (isSelf) return `/build-app/${entry.entity.id}`;
  return null;
}

/**
 * The entry's sentence, with the entity name emphasised. The server renders
 * plain text; the entity name is located in it rather than re-templated here,
 * so client and server can never disagree about the wording.
 */
function Sentence({ text, entityName, href }) {
  const i = entityName ? text.lastIndexOf(entityName) : -1;
  if (i < 0) return <>{text}</>;
  const name = href ? <Link to={href}>{entityName}</Link> : <strong>{entityName}</strong>;
  return <>{text.slice(0, i)}{name}{text.slice(i + entityName.length)}</>;
}

function Row({ entry, compact, isSelf, canReviewApps }) {
  const href = entryLink(entry, { isSelf, canReviewApps });
  return (
    <li className={`act-row act-row--${entry.direction}`}>
      <span className="act-row__dot" aria-hidden="true" />
      <time className="act-row__time" dateTime={entry.at}>{formatTime(entry.at)}</time>
      <div className="act-row__body">
        <span className="act-row__text">
          <span className="sr-only">{entry.direction === 'about' ? 'Done to this account: ' : 'Done by this person: '}</span>
          {/* A user entity is the person themselves -- emphasising their own
              name on their own timeline carries no information. */}
          <Sentence text={entry.text} entityName={entry.entity?.type === 'user' ? null : entry.entity?.name} href={href} />
        </span>
        {!compact && <span className="act-row__tag">{CATEGORY_LABELS[entry.category] || entry.category}</span>}
        {!compact && entry.details && (
          <details className="act-row__details">
            <summary><Chevron className="act-row__chevron" />Details</summary>
            <p>{entry.details}</p>
          </details>
        )}
      </div>
    </li>
  );
}

/**
 * A day-grouped activity list. Shared by the profile Activity tab and the
 * admin user drawer (compact: no category tag, no audit details).
 */
export default function ActivityList({ entries, compact = false, isSelf = false, canReviewApps = false }) {
  return (
    <div className={`act-list${compact ? ' act-list--compact' : ''}`}>
      {groupByDay(entries).map((g) => (
        <section key={g.label} className="act-day" aria-label={g.label}>
          <h3 className="act-day__label">{g.label}</h3>
          <ul className="act-day__rows">
            {g.items.map((e) => (
              <Row key={e.id} entry={e} compact={compact} isSelf={isSelf} canReviewApps={canReviewApps} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
